// src/pages/TransportationDashboardPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import api from "../api";
import TransportationModal from "../components/TransportationModal";
import DriverOutreachModal from "../components/DriverOutreachModal";

// Unified transportation-request status metadata (the single status staff see).
const STATUS_META = {
  Open:      { label: "Open",      bg: "warning",   text: "dark" },
  Approved:  { label: "Approved",  bg: "info",      text: "dark" },
  Confirmed: { label: "Confirmed", bg: "primary",   text: undefined },
  Assigned:  { label: "Assigned",  bg: "success",   text: undefined },
  Complete:  { label: "Complete",  bg: "secondary", text: undefined },
  Closed:    { label: "Closed",    bg: "secondary", text: undefined },
  Denied:    { label: "Denied",    bg: "danger",    text: undefined },
  Cancelled: { label: "Cancelled", bg: "danger",    text: undefined },
  // Fallback for ad-hoc rides with no linked Service Request (raw ride status).
  REQUESTED: { label: "Requested", bg: "warning",   text: "dark" },
  ASSIGNED:  { label: "Assigned",  bg: "success",   text: undefined },
  CONFIRMED: { label: "Confirmed", bg: "primary",   text: undefined },
  COMPLETED: { label: "Completed", bg: "secondary", text: undefined },
  CANCELLED: { label: "Cancelled", bg: "danger",    text: undefined },
  NO_SHOW:   { label: "No Show",   bg: "dark",      text: undefined },
};

// Summary cards use the full unified lifecycle order (so the period totals show
// how many requests reached terminal states too).
const ALL_STATUSES = [
  "Open", "Approved", "Confirmed", "Assigned", "Complete", "Closed",
  "Denied", "Cancelled",
];

// Terminal statuses are hidden from the working list — the dashboard is for
// active requests. They still count toward the summary cards above.
const HIDDEN_STATUSES = ["Complete", "Closed", "Denied", "Cancelled"];

// The Status filter only offers statuses that can actually appear in the table.
const ACTIVE_STATUSES = ALL_STATUSES.filter((s) => !HIDDEN_STATUSES.includes(s));

// Some legacy rows store the unified status in a different case (e.g. "complete"
// vs "Complete"). Canonicalize to the lifecycle spelling so counting, hiding and
// the badge all agree regardless of how the value was written.
const CANON_BY_LOWER = Object.fromEntries(
  ALL_STATUSES.map((s) => [s.toLowerCase(), s])
);
function canonStatus(s) {
  if (!s) return s;
  return CANON_BY_LOWER[String(s).toLowerCase()] || s;
}

// The unified status for a ride: the linked Service Request's status when there
// is one, else the raw ride status — canonicalized to the lifecycle spelling.
function unifiedOf(ride) {
  return canonStatus(ride.unified_status || ride.status);
}

// Local YYYY-MM-DD (no timezone math — these feed the date <input> and the
// backend's pickup_datetime range filter).
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// First → last day of the current month.
function monthBounds() {
  const now = new Date();
  return [
    ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
    ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  ];
}

// Jan 1 → today of the current year.
function ytdBounds() {
  const now = new Date();
  return [ymd(new Date(now.getFullYear(), 0, 1)), ymd(now)];
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, bg: "secondary" };
  return (
    <Badge bg={m.bg} text={m.text} style={m.style}>
      {m.label}
    </Badge>
  );
}

// Convert a 24-hour "HH:MM" string to 12-hour "H:MM AM/PM" without any
// timezone math (the source datetime is already wall-clock local).
function to12Hour(hhmm) {
  if (!hhmm) return "";
  const [hStr, mStr] = hhmm.split(":");
  let h = Number(hStr);
  if (Number.isNaN(h)) return hhmm;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mStr ?? "00"} ${ampm}`;
}

function formatDateTime(dt) {
  if (!dt) return "—";
  const s = String(dt).slice(0, 16).replace("T", " ");
  // Convert to MM/DD/YYYY H:MM AM/PM
  const [date, time] = s.split(" ");
  if (!date) return s;
  const [y, m, d] = date.split("-");
  return `${m}/${d}/${y} ${to12Hour(time)}`.trim();
}

function formatDate(dt) {
  if (!dt) return "—";
  const s = String(dt).slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${m}/${d}/${y}`;
}

function primaryAssignment(ride) {
  return (ride.assignments || []).find(
    (x) => x.role === "PRIMARY" && x.status !== "CANCELLED"
  );
}

// Driver cell: name, then every number we have — the primary from the driver
// record plus any additional numbers (named, for couples), so a coordinator can
// reach them without opening the driver or outreach modal.
function DriverCell({ ride }) {
  const a = primaryAssignment(ride);
  if (!a) return "—";
  return (
    <>
      <div>{a.driver_name || `Driver #${a.driver_id}`}</div>
      {a.driver_phone && (
        <div className="text-muted" style={{ fontSize: "0.85em" }}>
          {a.driver_phone}
        </div>
      )}
      {(a.driver_phones || []).map((p) => (
        <div key={p.id} className="text-muted" style={{ fontSize: "0.85em" }}>
          <i
            className={`bi ${p.type === "LANDLINE" ? "bi-telephone-fill" : "bi-phone-fill"} me-1`}
            title={p.type === "LANDLINE" ? "Landline" : "Mobile"}
          />
          {p.phone_number}
          {p.name ? ` · ${p.name}` : ""}
        </div>
      ))}
    </>
  );
}

function ConfirmedIcon({ value }) {
  return value ? (
    <i className="bi bi-check-circle-fill text-success" title="Yes" />
  ) : (
    <i className="bi bi-x-circle text-muted" title="No" />
  );
}

// Summary stat card
function StatCard({ label, count, bg, textColor }) {
  return (
    <Card className="text-center h-100">
      <Card.Body className="py-3">
        <div
          className="fw-bold"
          style={{ fontSize: "2rem", color: `var(--bs-${bg})` }}
        >
          {count}
        </div>
        <div className="text-muted" style={{ fontSize: "0.85rem" }}>
          {label}
        </div>
      </Card.Body>
    </Card>
  );
}

export default function TransportationDashboardPage() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Table filters — all default to "off" so the list shows every request unless
  // staff deliberately narrows it. (The date window here filters the TABLE only.)
  const [filterStatus, setFilterStatus] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [filterClient, setFilterClient] = useState("");
  // Summary-card period — independent of the table. "month" | "ytd".
  const [period, setPeriod] = useState("month");

  // Transport modal
  const [showModal, setShowModal] = useState(false);
  const [editingRide, setEditingRide] = useState(null);

  // Driver outreach modal (opened directly from a row)
  const [outreachRide, setOutreachRide] = useState(null);

  // Tracks the ride whose appointment is currently being verified (for the
  // in-row spinner / to prevent double clicks).
  const [verifyingId, setVerifyingId] = useState(null);

  const loadRides = async () => {
    setLoading(true);
    setError("");
    try {
      // Load every request; all filtering (table + summary period) is done
      // client-side so the list isn't narrowed by default.
      const res = await api.get("/drivers/rides");
      setRides(res.data.items || []);
    } catch (e) {
      setError("Failed to load transportation requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table rows: hide terminal requests, then apply the (off-by-default) status,
  // client and date-range filters. No date scoping unless staff sets it.
  const filteredRides = useMemo(() => {
    const q = filterClient.trim().toLowerCase();
    return rides.filter((r) => {
      if (HIDDEN_STATUSES.includes(unifiedOf(r))) return false;
      if (filterStatus && unifiedOf(r) !== filterStatus) return false;
      if (q && !(r.client_name || "").toLowerCase().includes(q)) return false;
      const day = (r.pickup_datetime || "").slice(0, 10);
      if (filterStart && (!day || day < filterStart)) return false;
      if (filterEnd && (!day || day > filterEnd)) return false;
      return true;
    });
  }, [rides, filterClient, filterStatus, filterStart, filterEnd]);

  // Summary cards are scoped to the chosen period (current month / YTD) by the
  // date the request was created — independent of the table filters. Scoping by
  // creation (intake) date rather than pickup date keeps upcoming requests
  // (Approved/Confirmed, whose appointments are often next month) in the count.
  const statusCounts = useMemo(() => {
    const [pStart, pEnd] = period === "ytd" ? ytdBounds() : monthBounds();
    const counts = {};
    for (const s of ALL_STATUSES) counts[s] = 0;
    for (const r of rides) {
      const day = (r.created_at || "").slice(0, 10);
      if (!day || day < pStart || day > pEnd) continue;
      const s = unifiedOf(r);
      if (counts[s] !== undefined) counts[s]++;
    }
    return counts;
  }, [rides, period]);

  const handleEdit = (ride) => {
    setEditingRide(ride);
    setShowModal(true);
  };

  // Outreach is driver-assignment work, so it's gated behind a verified
  // appointment — the same gate the assign endpoint enforces.
  const handleOutreach = (ride) => {
    if (!ride.appointment_confirmed) {
      // eslint-disable-next-line no-alert
      window.alert("Appointment must be Verified first!");
      return;
    }
    setOutreachRide(ride);
  };

  // Verify the appointment for a ride (sets appointment_confirmed = 1). This is
  // the gate that must be cleared before a driver can be assigned.
  const handleVerify = async (ride) => {
    setVerifyingId(ride.id);
    setError("");
    try {
      await api.patch(`/drivers/rides/${ride.id}`, { appointment_confirmed: true });
      await loadRides();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to verify the appointment.");
    } finally {
      setVerifyingId(null);
    }
  };

  const handleModalSaved = () => {
    setShowModal(false);
    setEditingRide(null);
    loadRides();
  };

  const clearFilters = () => {
    setFilterStatus("");
    setFilterStart("");
    setFilterEnd("");
    setFilterClient("");
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Transportation Dashboard</h3>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Summary period toggle — scopes the cards only, not the list below */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-muted small">
          Summary — {period === "ytd" ? "Year to Date" : "This Month"}
        </span>
        <ButtonGroup size="sm">
          <Button
            variant={period === "month" ? "primary" : "outline-primary"}
            onClick={() => setPeriod("month")}
          >
            This Month
          </Button>
          <Button
            variant={period === "ytd" ? "primary" : "outline-primary"}
            onClick={() => setPeriod("ytd")}
          >
            Year to Date
          </Button>
        </ButtonGroup>
      </div>

      {/* Summary cards */}
      <Row className="g-2 mb-4">
        {ALL_STATUSES.map((s) => {
          const m = STATUS_META[s];
          return (
            <Col key={s} xs={6} sm={4} md={2}>
              <StatCard
                label={m.label}
                count={statusCounts[s] ?? 0}
                bg={m.bg}
              />
            </Col>
          );
        })}
      </Row>

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="mb-1">Status</Form.Label>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {ACTIVE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_META[s].label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="mb-1">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filterStart}
                  onChange={(e) => setFilterStart(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="mb-1">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filterEnd}
                  onChange={(e) => setFilterEnd(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="mb-1">Client Name</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search client…"
                    value={filterClient}
                    onChange={(e) => setFilterClient(e.target.value)}
                  />
                  {filterClient && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setFilterClient("")}
                    >
                      &times;
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={clearFilters}
                className="w-100"
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Rides table */}
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Card>
          <Card.Body className="p-0">
            <Table striped bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Client</th>
                  <th>Date / Time</th>
                  <th>Destination</th>
                  <th>Provider</th>
                  <th>Driver</th>
                  <th>Appt</th>
                  <th>Driver</th>
                  <th>Status</th>
                  <th colSpan={2}>Actions</th>
                </tr>
                <tr>
                  <th colSpan={6}></th>
                  <th
                    className="text-center text-muted"
                    style={{ fontSize: "0.75em" }}
                    title="Appointment Verified"
                  >
                    Verified
                  </th>
                  <th
                    className="text-center text-muted"
                    style={{ fontSize: "0.75em" }}
                    title="Driver Confirmed"
                  >
                    Confirmed
                  </th>
                  <th colSpan={3}></th>
                </tr>
              </thead>
              <tbody>
                {filteredRides.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-muted py-3">
                      No transportation requests found.
                    </td>
                  </tr>
                )}
                {filteredRides.map((r) => (
                  <tr key={r.id}>
                    <td className="text-muted" style={{ fontSize: "0.85em" }}>
                      {r.id}
                    </td>
                    <td className="fw-semibold">
                      {r.client_id ? (
                        <Link to={`/clients/${r.client_id}/view`}>
                          {r.client_name || `Client #${r.client_id}`}
                        </Link>
                      ) : (
                        r.client_name || "—"
                      )}
                      {r.client_emergency_contact_name && (
                        <div className="text-muted fw-normal" style={{ fontSize: "0.8em" }}>
                          <i className="bi bi-exclamation-circle me-1" />
                          {r.client_emergency_contact_name}
                          {r.client_emergency_contact_phone ? ` · ${r.client_emergency_contact_phone}` : ""}
                        </div>
                      )}
                      {r.client_driver_notes && (
                        <div
                          className="text-muted fw-normal text-truncate"
                          style={{ fontSize: "0.8em", maxWidth: 220 }}
                          title={r.client_driver_notes}
                        >
                          <i className="bi bi-sticky me-1" />
                          {r.client_driver_notes}
                        </div>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(r.pickup_datetime)}
                    </td>
                    <td>{r.dropoff_location_text || "—"}</td>
                    <td>
                      {r.provider ? (
                        <>
                          <div>{r.provider.name}</div>
                          {r.provider.phone && (
                            <div
                              className="text-muted"
                              style={{ fontSize: "0.85em" }}
                            >
                              {r.provider.phone}
                            </div>
                          )}
                          {r.provider.city && (
                            <div
                              className="text-muted"
                              style={{ fontSize: "0.85em" }}
                            >
                              {r.provider.city}
                            </div>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <DriverCell ride={r} />
                    </td>
                    <td className="text-center">
                      <ConfirmedIcon value={r.appointment_confirmed} />
                    </td>
                    <td className="text-center">
                      <ConfirmedIcon value={r.driver_confirmed} />
                    </td>
                    <td>
                      <StatusBadge status={unifiedOf(r)} />
                    </td>
                    <td className="text-nowrap">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleEdit(r)}
                      >
                        Edit
                      </Button>
                      {!r.appointment_confirmed && (
                        <Button
                          size="sm"
                          variant="success"
                          className="ms-1"
                          onClick={() => handleVerify(r)}
                          disabled={verifyingId === r.id}
                        >
                          {verifyingId === r.id && (
                            <Spinner size="sm" animation="border" className="me-1" />
                          )}
                          Verify
                        </Button>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleOutreach(r)}
                      >
                        Outreach
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
          {filteredRides.length > 0 && (
            <Card.Footer className="text-muted" style={{ fontSize: "0.85em" }}>
              Showing {filteredRides.length} request
              {filteredRides.length !== 1 ? "s" : ""}
              {filterStatus || filterStart || filterEnd || filterClient
                ? " (filtered)"
                : ""}
            </Card.Footer>
          )}
        </Card>
      )}

      {/* Edit modal — no clientData needed since we're editing existing */}
      <TransportationModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingRide(null);
        }}
        onSaved={handleModalSaved}
        clientId={editingRide?.client_id}
        clientData={null}
        editRide={editingRide}
      />

      <DriverOutreachModal
        show={!!outreachRide}
        onHide={() => setOutreachRide(null)}
        ride={outreachRide}
        onAssigned={loadRides}
      />
    </>
  );
}
